import { z } from 'zod';
import { defineListQuery, inForceFilter } from './list-query.schemas';
import { VAT_TREATMENTS } from './invoice.schemas';

/**
 * The standard VAT rate, versioned by effective date (gitaumoses4/chukua#133). Zero-rated and exempt
 * carry no rate — they are 0 by definition, enforced by `invoice_lines_vat_check` (0053_invoices.sql) —
 * so only the standard rate is ever superseded. Company-wide: there is no per-customer tax status.
 */
const vatRateBpsSchema = z.number().int().min(0).max(10_000);

/** `POST /vat-rates/:id/supersede` — a price change closes the old rate the day before and opens a new one. */
export const supersedeVatRateInputSchema = z.object({
  rateBps: vatRateBpsSchema,
  effectiveDate: z.iso.date(),
});
export type SupersedeVatRateInput = z.infer<typeof supersedeVatRateInputSchema>;

/** `POST /vat-rates/:id/end` — last day the rate applies, inclusive. */
export const endVatRateInputSchema = z.object({ validTo: z.iso.date() });
export type EndVatRateInput = z.infer<typeof endVatRateInputSchema>;

export const vatRateListQuerySchema = defineListQuery({
  sortFields: ['validFrom', 'rateBps', 'createdAt'],
  defaultSort: { sort: 'validFrom', order: 'desc' },
  filters: { current: inForceFilter },
});
export type VatRateListQuery = z.infer<typeof vatRateListQuerySchema>;

/**
 * `POST /invoice-lines/:id/vat-override` — the treatment to use instead of the default and why. The default
 * comes from the job recorded on the line, never from the request. A treatment that matches the
 * default is refused: there is nothing to override.
 */
export const vatLineOverrideInputSchema = z.object({
  treatment: z.enum(VAT_TREATMENTS),
  reason: z.string().trim().min(1).max(500),
});
export type VatLineOverrideInput = z.infer<typeof vatLineOverrideInputSchema>;
